import React, { useContext, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { PageSettings } from "../../Config/PageSettings";
import useInput from "../../Hooks/useInput";
import { useMutation, useApolloClient } from "react-apollo-hooks";
import {
  LOG_IN,
  LOCAL_LOG_IN,
  ADMIN_INFO_QUERY,
  TOTP_VERIFY,
  TOTP_ENROLL_START,
  TOTP_ENROLL_CONFIRM,
} from "./Queries";
import Presenter from "./Presenter";
import { toast } from "react-toastify";
import { LOGIN_TYPES, isTypeAllowed } from "../../Config/viewType";

export default withRouter(({ history }) => {
  const {
    handleSetPageSidebar,
    handleSetPageHeader,
    handleSetPageContentFullWidth,
  } = useContext(PageSettings);

  const userId = useInput("");
  const password = useInput("");
  // 로그인 유형: 마지막 선택값 기억(없으면 관리자)
  const [loginType, setLoginType] = useState(
    localStorage.getItem("loginType") || "Administrator"
  );

  // 화면 단계: credentials → (totp | enroll)
  // 관리자(Administrator)이면서 2차 인증 대상일 때만 2단계로 넘어간다.
  const [step, setStep] = useState("credentials");
  const [challenge, setChallenge] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const client = useApolloClient();
  const [logInMutation] = useMutation(LOG_IN);
  const [localLogInMutation] = useMutation(LOCAL_LOG_IN);
  const [totpVerifyMutation] = useMutation(TOTP_VERIFY);
  const [totpEnrollStartMutation] = useMutation(TOTP_ENROLL_START);
  const [totpEnrollConfirmMutation] = useMutation(TOTP_ENROLL_CONFIRM);

  const showError = (e) => {
    const error = String(e).replace("Error: GraphQL error:", "").trim();
    toast.error(error || "로그인에 실패했습니다.");
  };

  // 세션 토큰을 받은 뒤 공통 처리:
  // 선택한 로그인 유형이 계정 권한과 맞는지 확인하고 로그인을 확정한다.
  const finishLogin = async (token) => {
    localStorage.setItem("token", token);
    const { data } = await client.query({
      query: ADMIN_INFO_QUERY,
      fetchPolicy: "network-only",
    });
    const info = data && data.AdminInfo;
    if (!isTypeAllowed(loginType, info)) {
      localStorage.removeItem("token");
      const typeObj = LOGIN_TYPES.find((t) => t.id === loginType);
      toast.error(
        `${typeObj ? typeObj.name : "선택한 유형"}(으)로 로그인할 수 없는 계정입니다.`
      );
      resetToCredentials();
      return;
    }
    localStorage.setItem("loginType", loginType);
    localLogInMutation({ variables: { token } });
  };

  const resetToCredentials = () => {
    setStep("credentials");
    setChallenge(null);
    setEnroll(null);
    setTotpCode("");
  };

  // 1단계: 아이디·비밀번호
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (userId.value === "" || password.value === "") {
      toast.error("아이디 비밀번호를 입력해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      const {
        data: { AdminLogIn: result },
      } = await logInMutation({
        variables: { userId: userId.value, password: password.value },
      });

      if (!result) {
        throw Error();
      }

      // 2차 인증이 필요 없는 계정은 종전과 동일하게 바로 로그인
      if (!result.totpRequired) {
        if (!result.token) {
          throw Error();
        }
        await finishLogin(result.token);
        return;
      }

      setChallenge(result.challenge);

      if (result.totpEnrolled) {
        setStep("totp");
        return;
      }

      // 미등록 관리자 → 인증 앱 등록부터
      const {
        data: { AdminTotpEnrollStart: started },
      } = await totpEnrollStartMutation({
        variables: { challenge: result.challenge },
      });
      setEnroll(started);
      setStep("enroll");
    } catch (e) {
      localStorage.removeItem("token");
      showError(e);
    } finally {
      setSubmitting(false);
    }
  };

  // 2단계: 인증 코드(또는 복구코드)
  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (!totpCode.trim()) {
      toast.error("인증 코드를 입력해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      if (step === "enroll") {
        const {
          data: { AdminTotpEnrollConfirm: token },
        } = await totpEnrollConfirmMutation({
          variables: { challenge, code: totpCode.trim() },
        });
        await finishLogin(token);
      } else {
        const {
          data: { AdminTotpVerify: token },
        } = await totpVerifyMutation({
          variables: { challenge, code: totpCode.trim() },
        });
        await finishLogin(token);
      }
    } catch (e) {
      localStorage.removeItem("token");
      setTotpCode("");
      showError(e);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    handleSetPageSidebar(false);
    handleSetPageHeader(false);
    handleSetPageContentFullWidth(true);
    return () => {
      handleSetPageSidebar(true);
      handleSetPageHeader(true);
      handleSetPageContentFullWidth(false);
    };
  }, [
    handleSetPageSidebar,
    handleSetPageHeader,
    handleSetPageContentFullWidth,
  ]);

  return (
    <Presenter
      userId={userId}
      password={password}
      handleSubmit={handleSubmit}
      loginType={loginType}
      setLoginType={setLoginType}
      loginTypes={LOGIN_TYPES}
      step={step}
      enroll={enroll}
      totpCode={totpCode}
      setTotpCode={setTotpCode}
      handleTotpSubmit={handleTotpSubmit}
      onCancelTotp={resetToCredentials}
      submitting={submitting}
    />
  );
});
